'use client'

import { useState } from 'react'

import { usePathname, useRouter, useSearchParams } from 'next/navigation'

import { Checkbox } from '@/app/components/atoms/forms/Checkbox'
import type { RangeSliderValue } from '@/app/components/shared/forms/RangeSliderField'
import { ListingFilters, toSelectOptions } from '@/app/components/shared/ListingFilters'
import { useOptionalPageAnalytics } from '@/app/context/analytics'
import { FILTERED_LISTING_MODULE_TYPE } from '@/app/lib/analytics/constants'
import { ANALYTICS_SUBGENRE_FILTER_TYPE } from '@/app/entradas/(general-web)/agenda/_components/constants'
import {
  countActiveFilters,
  formatPriceFilterValue,
  type ActiveFilterState,
} from '@/app/lib/analytics/listingFilterTracking'
import type { SanitizedAppliedFilters } from '@/app/lib/api/sanitizeAppliedFilters'
import type { LoMasVendidoFilters as LoMasVendidoFiltersData } from '@/app/lib/api/types-api'
import { ALL_LOCATIONS, PAGE_PARAM } from '@/app/lib/constants'
import { setManualLocation } from '@/app/lib/geolocation'
import { invalidateResolvedTargetCache } from '@/app/lib/hooks/useGeoLocationSync'
import { useListingFieldFilters } from '@/app/lib/hooks/useListingFieldFilters'
import { routes } from '@/app/lib/routing'

import {
  ANALYTICS_MODULE_NAME,
  ANALYTICS_PAGE_TYPE,
} from '@/app/entradas/(general-web)/lo-mas-vendido/[location]/_components/constants'
import { READABLE_FILTER_PARAMS } from '@/app/entradas/(general-web)/lo-mas-vendido/[location]/_components/parseLoMasVendidoFilters'

interface LoMasVendidoFiltersProps {
  /** Provincia actual (segmento `[location]`); fuente de verdad del filtro de provincia (FR-026). */
  readonly location: string
  readonly filters: LoMasVendidoFiltersData
  /** Selección activa ya validada contra `filters` (FR-028). */
  readonly appliedFilters: SanitizedAppliedFilters
  /** Total de eventos de la consulta actual (`meta.total`), mostrado en "Aplicar filtros (N)". */
  readonly totalResults: number
}

/** Orden fijo de los filtros con dropdown de `fields` que quedan tras sacar
 * Precio a `rangeField` (revisado — ver comentario de la propia función más
 * abajo): Categoría y Subcategoría. El genérico `onApply` de `ListingFilters` entrega
 * los valores del borrador por posición, así que este orden traduce esa
 * posición de vuelta a su query param real. */
const FIELD_ORDER = ['genre', 'subgenre'] as const

/**
 * Resuelve qué extremos del rango de Precio viajan a la URL legible: si el
 * rango seleccionado coincide con el rango COMPLETO de `price_range` (el
 * usuario no ha tocado el slider), se omiten los dos — equivalente a "sin
 * filtro". Mismo criterio que `PromocionesFilters`.
 */
function resolvePriceParams(
  range: RangeSliderValue,
  bounds: RangeSliderValue,
): { min: number | undefined; max: number | undefined } {
  const isFullRange = range.min === bounds.min && range.max === bounds.max
  return isFullRange ? { min: undefined, max: undefined } : { min: range.min, max: range.max }
}

/**
 * `LoMasVendidoFilters` — envoltorio fino sobre `ListingFilters` (DS
 * compartido): declara los filtros de esta página (provincia, categoría,
 * fecha, precio — FR-018) y cómo cada uno se refleja en la URL. El selector
 * de provincia navega a `/lo-mas-vendido/{provincia}` preservando el resto de
 * filtros pero sin `page` (FR-026/FR-027) — es el `instantField` de
 * `ListingFilters`, ya que aplica al instante tanto en desktop como dentro
 * del SideSheet, sin pasar por su borrador.
 *
 * Fecha se maqueta como chips de selección única (`dateField`, no un
 * dropdown): aplicación instantánea igual que `instantField`, siempre visible
 * bajo los dropdowns (y en la franja sticky), nunca dentro del borrador del
 * SideSheet.
 *
 * Precio (revisado — mismo mecanismo que `PromocionesFilters`, unifica la
 * maquetación del selector de precio en todas las PLP del sitio salvo
 * `/search`) pasó de un `min_price` de tramo único (`fields[]`, aplicado vía
 * el borrador del SideSheet) a un rango CONTINUO acotado por
 * `filters.price_range`, modelado como el `rangeField` de `ListingFilters`
 * (slider de doble asa), de aplicación instantánea al soltar el slider, fuera
 * del borrador. Junto al slider, un checkbox "Mostrar solo gratuitos"
 * (`extra`) — visible solo si la clave `filters.free_filter` está presente en
 * la respuesta — navega con `free_filter=on` y deshabilita el slider
 * (`rangeField.sliderDisabled`, el propio `extra` sigue alcanzable para poder
 * desmarcarlo). Categoría es el único `field` restante del borrador del
 * SideSheet.
 */
export function LoMasVendidoFilters({ location, filters, appliedFilters, totalResults }: LoMasVendidoFiltersProps) {
  const router = useRouter()
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const analytics = useOptionalPageAnalytics()
  const { values, setFilter, setFilters } = useListingFieldFilters({
    paramNames: READABLE_FILTER_PARAMS,
    pageParam: PAGE_PARAM,
  })
  // Recuerda el rango previo a marcar "Mostrar solo gratuitos", para
  // restaurarlo al desmarcar (en vez de volver a los límites completos) —
  // mismo patrón que `PromocionesFilters`.
  const [rangeBeforeFreeOnly, setRangeBeforeFreeOnly] = useState<RangeSliderValue | null>(null)

  // Mismo criterio que `PromocionesFilters`: los límites del slider se
  // congelan en cuanto el filtro de precio se aplica, para no estrecharse con
  // la propia respuesta ya acotada por la selección vigente. `isFreeFilterActive`
  // cuenta igual que `isPriceFilterActive`: con "Mostrar solo gratuitos" activo
  // el backend acota `price_range` a `[0, 0]`, y dejarlo pasar clava los
  // límites reales del slider en cero (bug detectado 2026-09-09: los thumbs se
  // juntan al desmarcar y no se pueden separar).
  const isPriceFilterActive = values.priceMin !== '' || values.priceMax !== ''
  const isFreeFilterActive = values.freeFilter === 'on'
  const showFreeFilterCheckbox = Boolean(filters.free_filter)
  const [stableBounds, setStableBounds] = useState<RangeSliderValue>(filters.price_range)
  if (
    !isPriceFilterActive &&
    !isFreeFilterActive &&
    (stableBounds.min !== filters.price_range.min || stableBounds.max !== filters.price_range.max)
  ) {
    setStableBounds(filters.price_range)
  }
  const priceBounds = stableBounds
  // Con "Mostrar solo gratuitos" activo la URL no lleva `price_min`/
  // `price_max` (`resolvePriceParams` los omite al coincidir con el rango
  // completo), solo `free_filter=on` — sin este caso especial,
  // `currentPriceRange` caería al `else` y devolvería `priceBounds` (rango
  // completo) en vez de `[0, 0]`, indistinguible de "sin filtro" para
  // `rangeField` (bug detectado 2026-09-11: ni el chip de filtro aplicado ni
  // el texto del desplegable, "Gratuitos", podían reaccionar).
  const currentPriceRange: RangeSliderValue = isFreeFilterActive
    ? { min: 0, max: 0 }
    : {
        min: values.priceMin !== '' ? Number(values.priceMin) : priceBounds.min,
        max: values.priceMax !== '' ? Number(values.priceMax) : priceBounds.max,
      }

  // ── Analítica (`filter_change`, `Listado filtrado.csv` → «Cambio de filtro»)
  // Cada handler emite el evento ANTES de navegar: la navegación posterior
  // re-dispara el `page_view` con la nueva jerarquía (guía 02 §9), así que el
  // orden importa. Fuera del `PageAnalyticsProvider` (tests), `analytics` es
  // `null` y los filtros siguen funcionando sin tracking.

  /** Estado de filtros vigente, base para calcular `filters_active_count`. */
  const currentFilterState: ActiveFilterState = {
    province: location,
    genre: values.genre,
    subgenre: values.subgenre,
    dateRange: values.dateRange,
    priceActive: isPriceFilterActive || isFreeFilterActive,
  }

  function emitFilterChange(change: {
    type: string
    action: string
    value: string
    previousValue: string
    /** Estado de filtros RESULTANTE del cambio (no el previo). */
    next: ActiveFilterState
  }) {
    analytics?.trackFilterChange({
      filter_action: change.action,
      filter_type: change.type,
      filter_value: change.value,
      filter_previous_value: change.previousValue,
      filters_active_count: String(countActiveFilters(change.next)),
      module_name: ANALYTICS_MODULE_NAME,
      module_type: FILTERED_LISTING_MODULE_TYPE,
    })
  }

  async function handleProvinceChange(newLocation: string) {
    // `emptyValue: ALL_LOCATIONS` — al limpiar el selector llega `todos`, que es
    // "sin filtrar": `remove`, no `clear_all` (eso lo reserva "Limpiar todo").
    // Solo se emite en el cambio MANUAL del selector: la geolocalización
    // automática (`GeoLocationSync`) también navega a una provincia, pero no es
    // una interacción del usuario con el filtro y contarla infla el evento.
    const target = newLocation || ALL_LOCATIONS
    emitFilterChange({
      type: 'provincia',
      action: target === ALL_LOCATIONS ? 'remove' : 'apply',
      value: target,
      previousValue: location === ALL_LOCATIONS ? 'not_set' : location,
      next: { ...currentFilterState, province: target },
    })
    // Persiste la elección manual en la cookie `location` (specs/025-geolocalizacion-web-general)
    // — se espera ANTES de navegar: si no, `useGeoLocationSync` relee la cookie
    // anterior en la página destino y redirige de vuelta a la provincia previa
    // (limpiar el filtro no surtía efecto).
    await setManualLocation(newLocation || ALL_LOCATIONS)
    invalidateResolvedTargetCache()
    const next = new URLSearchParams(searchParams.toString())
    next.delete(PAGE_PARAM)
    const qs = next.toString()
    router.push(qs ? `${routes.loMasVendido(newLocation)}?${qs}` : routes.loMasVendido(newLocation))
  }

  function handleGenreChange(value: string) {
    emitFilterChange({
      type: 'categoria',
      action: value ? 'apply' : 'remove',
      value: value || 'not_set',
      previousValue: String(appliedFilters.genre_nicename ?? '') || 'not_set',
      next: { ...currentFilterState, genre: value, subgenre: '' },
    })
    // Cambiar o quitar la categoría invalida la subcategoría activa (pertenecía a la anterior).
    setFilters({ genre: value, subgenre: '' })
  }

  function handleSubgenreChange(value: string) {
    emitFilterChange({
      type: ANALYTICS_SUBGENRE_FILTER_TYPE,
      action: value ? 'apply' : 'remove',
      value: value || 'not_set',
      previousValue: String(appliedFilters.subgenre_nicename ?? '') || 'not_set',
      next: { ...currentFilterState, subgenre: value },
    })
    setFilter('subgenre', value)
  }

  function handleDateRangeChange(value: string) {
    emitFilterChange({
      type: 'fecha',
      action: value ? 'apply' : 'remove',
      value: value || 'not_set',
      previousValue: String(appliedFilters.date_range ?? '') || 'not_set',
      next: { ...currentFilterState, dateRange: value },
    })
    setFilter('dateRange', value)
  }

  function handleApply(applied: readonly string[]) {
    // SideSheet móvil: el borrador aplica Categoría y Subcategoría en una sola
    // navegación. Precio ya no forma parte de este borrador (ver `rangeField`
    // más abajo).
    const nextGenre = applied[FIELD_ORDER.indexOf('genre')] ?? ''
    // Si la categoría cambió y la subcategoría del borrador sigue siendo la
    // anterior, se descarta (pertenecía a la categoría previa).
    const genreChanged = nextGenre !== String(appliedFilters.genre_nicename ?? '')
    const subgenreUntouched = (applied[FIELD_ORDER.indexOf('subgenre')] ?? '') === String(appliedFilters.subgenre_nicename ?? '')
    const nextSubgenre = genreChanged && subgenreUntouched ? '' : (applied[FIELD_ORDER.indexOf('subgenre')] ?? '')
    const next: ActiveFilterState = { ...currentFilterState, genre: nextGenre, subgenre: nextSubgenre }

    const previousGenre = String(appliedFilters.genre_nicename ?? '')
    if (nextGenre !== previousGenre) {
      emitFilterChange({
        type: 'categoria',
        action: nextGenre ? 'apply' : 'remove',
        value: nextGenre || 'not_set',
        previousValue: previousGenre || 'not_set',
        next,
      })
    }

    const previousSubgenre = String(appliedFilters.subgenre_nicename ?? '')
    if (nextSubgenre !== previousSubgenre) {
      emitFilterChange({
        type: ANALYTICS_SUBGENRE_FILTER_TYPE,
        action: nextSubgenre ? 'apply' : 'remove',
        value: nextSubgenre || 'not_set',
        previousValue: previousSubgenre || 'not_set',
        next,
      })
    }

    const nextParams = new URLSearchParams(searchParams.toString())
    if (nextGenre) nextParams.set(READABLE_FILTER_PARAMS.genre, nextGenre)
    else nextParams.delete(READABLE_FILTER_PARAMS.genre)
    if (nextSubgenre) nextParams.set(READABLE_FILTER_PARAMS.subgenre, nextSubgenre)
    else nextParams.delete(READABLE_FILTER_PARAMS.subgenre)
    nextParams.delete(PAGE_PARAM)
    const qs = nextParams.toString()
    router.push(qs ? `${pathname}?${qs}` : pathname, { scroll: false })
  }

  function handlePriceChangeEnd(range: RangeSliderValue) {
    const { min, max } = resolvePriceParams(range, priceBounds)
    const isCleared = min === undefined
    emitFilterChange({
      type: 'precio',
      action: isCleared ? 'remove' : 'apply',
      value: isCleared ? 'not_set' : formatPriceFilterValue(range),
      previousValue: isPriceFilterActive ? formatPriceFilterValue(currentPriceRange) : 'not_set',
      next: { ...currentFilterState, priceActive: !isCleared },
    })
    // Tocar el slider directamente implica dejar "Mostrar solo gratuitos" (en
    // la práctica inalcanzable mientras está marcado — el propio slider queda
    // deshabilitado, `rangeField.sliderDisabled` — pero se resuelve igual aquí
    // por si acaso).
    setFilters({
      priceMin: min !== undefined ? String(min) : '',
      priceMax: max !== undefined ? String(max) : '',
      freeFilter: '',
    })
  }

  function handleFreeFilterChange(checked: boolean) {
    if (checked) {
      setRangeBeforeFreeOnly(currentPriceRange)
      emitFilterChange({
        type: 'precio',
        action: 'apply',
        value: 'gratis',
        previousValue: isPriceFilterActive ? formatPriceFilterValue(currentPriceRange) : 'not_set',
        next: { ...currentFilterState, priceActive: true },
      })
      setFilters({ priceMin: '', priceMax: '', freeFilter: 'on' })
      return
    }
    const restoredRange = rangeBeforeFreeOnly ?? priceBounds
    const { min, max } = resolvePriceParams(restoredRange, priceBounds)
    const isCleared = min === undefined
    emitFilterChange({
      type: 'precio',
      action: isCleared ? 'remove' : 'apply',
      value: isCleared ? 'not_set' : formatPriceFilterValue(restoredRange),
      previousValue: 'gratis',
      next: { ...currentFilterState, priceActive: !isCleared },
    })
    setFilters({
      priceMin: min !== undefined ? String(min) : '',
      priceMax: max !== undefined ? String(max) : '',
      freeFilter: '',
    })
  }

  function clearPriceRange() {
    // `rangeField.onClear` — lo invoca "Limpiar filtros" del SideSheet, de ahí
    // `clear_all` (el resto de campos se limpian en el borrador y viajan
    // después por `handleApply`; el precio vive fuera del borrador y navega ya).
    emitFilterChange({
      type: 'precio',
      action: 'clear_all',
      value: 'not_set',
      previousValue: isFreeFilterActive
        ? 'gratis'
        : isPriceFilterActive
          ? formatPriceFilterValue(currentPriceRange)
          : 'not_set',
      next: { ...currentFilterState, priceActive: false },
    })
    setRangeBeforeFreeOnly(null)
    setFilters({ priceMin: '', priceMax: '', freeFilter: '' })
  }

  function handleClearAll() {
    // "Limpiar todo" quita todos los filtros de golpe, pero `filter_type` es un
    // enum cerrado sin valor "todos": se emite un `filter_change` con
    // `filter_action=clear_all` por cada filtro que estaba activo (máx. 4),
    // todos con `filters_active_count=0`. Así ningún valor sale del enum y el
    // informe puede contar tanto la acción como qué se limpió. ⚠️ Pendiente de
    // confirmar con Analítica si prefieren un único evento (mismo criterio que
    // en Promociones — si cambia, cambia en las dos).
    const cleared: ActiveFilterState = {
      province: ALL_LOCATIONS,
      genre: '',
      subgenre: '',
      dateRange: '',
      priceActive: false,
    }
    const active: { type: string; previousValue: string }[] = []
    if (location !== ALL_LOCATIONS) active.push({ type: 'provincia', previousValue: location })
    if (values.genre) active.push({ type: 'categoria', previousValue: values.genre })
    if (values.subgenre) active.push({ type: ANALYTICS_SUBGENRE_FILTER_TYPE, previousValue: values.subgenre })
    if (values.dateRange) active.push({ type: 'fecha', previousValue: values.dateRange })
    if (isFreeFilterActive) {
      active.push({ type: 'precio', previousValue: 'gratis' })
    } else if (isPriceFilterActive) {
      active.push({ type: 'precio', previousValue: formatPriceFilterValue(currentPriceRange) })
    }

    for (const { type, previousValue } of active) {
      emitFilterChange({ type, action: 'clear_all', value: 'not_set', previousValue, next: cleared })
    }
  }

  return (
    <ListingFilters
      instantField={{
        label: 'Provincia',
        value: location,
        options: toSelectOptions(filters.province.items),
        placeholder: 'España',
        // El "sin filtrar" de provincia es "todos" (ALL_LOCATIONS), no cadena
        // vacía: con una provincia concreta seleccionada aparece el botón de
        // borrar (vuelve a "todos"); con "todos" ya seleccionado, no hay nada
        // que borrar y se muestra la flecha para poder elegir una.
        emptyValue: ALL_LOCATIONS,
        // Sin ninguna opción real entre la que elegir (`items: []` — la
        // combinación del resto de filtros activos no deja ninguna): todo el
        // campo queda deshabilitado, no solo sin opciones dentro del desplegable.
        disabled: filters.province.items.length === 0,
        onChange: handleProvinceChange,
      }}
      dateField={{
        label: 'Fecha',
        value: String(appliedFilters.date_range ?? ''),
        options: toSelectOptions(filters.date_range.items),
        onChange: handleDateRangeChange,
      }}
      fields={[
        {
          label: 'Categoría',
          value: String(appliedFilters.genre_nicename ?? ''),
          options: toSelectOptions(filters.genre.items),
          placeholder: 'Cualquiera',
          disabled: filters.genre.items.length === 0,
          onChange: handleGenreChange,
        },
        {
          label: 'Subcategoría',
          value: String(appliedFilters.subgenre_nicename ?? ''),
          options: toSelectOptions(filters.subgenre.items),
          placeholder: 'Cualquiera',
          disabled: filters.subgenre.items.length === 0,
          onChange: handleSubgenreChange,
        },
      ]}
      rangeField={{
        label: 'Precio',
        min: priceBounds.min,
        max: priceBounds.max,
        value: currentPriceRange,
        sliderDisabled: isFreeFilterActive,
        onChangeEnd: handlePriceChangeEnd,
        onClear: clearPriceRange,
        extra: showFreeFilterCheckbox
          ? ({ variant }) => (
              <Checkbox
                label="Mostrar solo gratuitos"
                checked={isFreeFilterActive}
                onChange={handleFreeFilterChange}
                inverse={variant === 'inverse'}
              />
            )
          : undefined,
      }}
      totalResults={totalResults}
      clearValues={['', '']}
      onApply={handleApply}
      clearAllHref={routes.loMasVendido(ALL_LOCATIONS)}
      onClearAll={handleClearAll}
      analytics={{
        moduleName: ANALYTICS_MODULE_NAME,
        moduleType: FILTERED_LISTING_MODULE_TYPE,
        pageType: ANALYTICS_PAGE_TYPE,
      }}
      showViewToggle
    />
  )
}
